import React from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  alpha,
  useTheme,
  Divider,
  Button,
  Alert,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import InfoIcon from "@mui/icons-material/Info";
import { formatAmount } from "../../../utils/hooks/dataHandler";
import CalculationSummary from "./CalculationSummary";
import TaxField from "../../utils/TextField/TaxField";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import BarChartIcon from "@mui/icons-material/BarChart";
import PublicIcon from "@mui/icons-material/Public";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const ForeignIncomeForm = ({
  canEdit = true,
  steps,
  total,
  taxAdjustment,
  netOtherIncome,
  grossIncome,
  allowableExpenses,
  foreignTaxSuffered,
  onForeignTaxSufferedChange,
  onTagToFTC,
  isTagToFTC,
  onUnTagToFTC,
}) => {
  const theme = useTheme();
  // Determine if we should show foreign tax input and tag to FTC functionality
  const shouldShowForeignTaxInput =
    taxAdjustment === "Added Back" && netOtherIncome > 0;

  const shouldShowButNegative =
    taxAdjustment === "Added Back" && netOtherIncome <= 0;

  const shouldShowButNotAddBack = taxAdjustment === "Unremitted Foreign Income";

  const isTagToFTCEnabled = shouldShowForeignTaxInput && foreignTaxSuffered > 0;

  const handleForeignTaxSufferedChange = (value) => {
    onForeignTaxSufferedChange?.(value);
  };

  const handleTagToFTC = () => {
    if (isTagToFTCEnabled) {
      onTagToFTC?.({
        netIncome: netOtherIncome,
        foreignTaxSuffered: foreignTaxSuffered,
        scheduleType: taxAdjustment,
      });
    }
  };

  // Show simplified view if no FTC functionality is needed
  if (!shouldShowForeignTaxInput && !shouldShowButNotAddBack) {
    return (
      <>
        {shouldShowButNegative && (
          <Alert
            severity='warning'
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-icon": {
                fontSize: 28,
              },
            }}
            action={
              <Tooltip title='Foreign Tax Credit is not applicable when net income is zero or negative'>
                <IconButton size='small' color='inherit'>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            }
          >
            <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
              Foreign Tax Credit Not Applicable
            </Typography>
            <Typography variant='body2'>
              Since the net income for this schedule is zero or negative,
              Foreign Tax Credit tagging and foreign tax inputs are disabled.
              This is because there's no positive income to apply foreign tax
              credits against.
            </Typography>
          </Alert>
        )}
        <CalculationSummary
          steps={steps}
          total={total}
          title='Income Calculation Summary'
          subtitle='Review the calculation breakdown below'
        />
      </>
    );
  }

  // Foreign Tax Credit Form (when FTC is applicable)
  return (
    <Box sx={{ width: "100%" }}>
      {/* Foreign Tax Input Section */}
      <Paper
        elevation={2}
        sx={{
          mb: 3,
          p: 4,
          borderRadius: 3,
          border: "2px solid",
          borderColor: alpha(theme.palette.primary.main, 0.3),
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.06
          )}, ${alpha(theme.palette.primary.main, 0.02)})`,
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${
              theme.palette.primary.main
            }, ${alpha(theme.palette.primary.main, 0.7)})`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 3,
            pb: 2,
            borderBottom: "2px solid",
            borderColor: alpha(theme.palette.primary.main, 0.2),
          }}
        >
          <Stack direction='row' spacing={2} alignItems='center' sx={{ mb: 1 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              <CurrencyExchangeIcon
                sx={{ fontSize: 26, color: "primary.main" }}
              />
            </Box>
            <Box>
              <Typography variant='h5' fontWeight={700} color='primary.main'>
                {shouldShowButNotAddBack
                  ? "Foreign Tax Suffered"
                  : "Foreign Tax Credit Processing"}
              </Typography>
              {!shouldShowButNotAddBack && (
                <Typography variant='body2' color='text.secondary'>
                  Step 1: Enter foreign tax amount • Step 2: Tag for FTC
                  calculation
                </Typography>
              )}
              {shouldShowButNotAddBack && (
                <Typography variant='body2' color='text.secondary'>
                  Enter foreign tax amount
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>

        {/* Foreign Tax Input */}
        <Box sx={{ mb: 4 }}>
          <Stack direction='row' spacing={2} alignItems='center' sx={{ mb: 2 }}>
            <Typography
              variant='h6'
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {shouldShowButNotAddBack
                ? "Foreign Tax Suffered"
                : "Step 1: Foreign Tax Suffered"}
            </Typography>
            <Tooltip title='Enter the amount of foreign tax you paid on this income. This will be used to calculate your Foreign Tax Credit.'>
              <IconButton size='small' sx={{ color: "text.secondary" }}>
                <InfoIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
          <Box
            sx={{
              p: 3,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <TaxField
              canEdit={canEdit}
              value={foreignTaxSuffered}
              onChange={handleForeignTaxSufferedChange}
              fullWidth={true}
              decimalScale={2}
              placeholder='Enter foreign tax amount...'
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "1.1rem",
                  fontWeight: 500,
                },
              }}
            />
            {canEdit && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mt: 1, display: "block" }}
              >
                <LightbulbIcon
                  sx={{ fontSize: 16, mr: 1, verticalAlign: "middle" }}
                />
                Tip: This should be the actual tax paid to the foreign country
                on this income
              </Typography>
            )}
          </Box>
        </Box>

        {/* Summary */}
        {foreignTaxSuffered > 0 && !shouldShowButNotAddBack && (
          <>
            <Divider sx={{ my: 3 }} />

            <Box>
              <Stack
                direction='row'
                spacing={2}
                alignItems='center'
                sx={{ mb: 3 }}
              >
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  Step 2: FTC Calculation Summary
                </Typography>
                <Chip
                  label='Ready for FTC'
                  color='success'
                  size='small'
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: alpha(theme.palette.success.main, 0.2),
                  }}
                >
                  <Box>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      fontWeight={500}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <AttachMoneyIcon sx={{ fontSize: 18, mr: 1 }} />
                      Gross Income
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Total income before expenses
                    </Typography>
                  </Box>
                  <Typography
                    variant='h6'
                    fontWeight={700}
                    color='success.main'
                  >
                    {formatAmount(grossIncome)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    bgcolor: alpha(theme.palette.error.main, 0.08),
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: alpha(theme.palette.error.main, 0.2),
                  }}
                >
                  <Box>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      fontWeight={500}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <TrendingDownIcon sx={{ fontSize: 18, mr: 1 }} />
                      Allowable Expenses
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Direct expenses related to income
                    </Typography>
                  </Box>
                  <Typography variant='h6' fontWeight={700} color='error.main'>
                    {formatAmount(allowableExpenses, true, true)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  }}
                >
                  <Box>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      fontWeight={500}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <BarChartIcon sx={{ fontSize: 18, mr: 1 }} />
                      Net Income
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Income available for FTC calculation
                    </Typography>
                  </Box>
                  <Typography
                    variant='h6'
                    fontWeight={700}
                    color='primary.main'
                  >
                    {formatAmount(netOtherIncome)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    bgcolor: alpha(theme.palette.warning.main, 0.08),
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: alpha(theme.palette.warning.main, 0.2),
                  }}
                >
                  <Box>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      fontWeight={500}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <PublicIcon sx={{ fontSize: 18, mr: 1 }} />
                      Foreign Tax Suffered
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Tax paid to foreign country
                    </Typography>
                  </Box>
                  <Typography
                    variant='h6'
                    fontWeight={700}
                    color='warning.dark'
                  >
                    {formatAmount(foreignTaxSuffered, false)}
                  </Typography>
                </Box>
              </Stack>

              {/* Tag to FTC Button */}
              <Box sx={{ mt: 4 }}>
                {isTagToFTC ? (
                  <Box
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      border: `3px solid ${alpha(
                        theme.palette.success.main,
                        0.4
                      )}`,
                      textAlign: "center",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${
                          theme.palette.success.main
                        }, ${alpha(theme.palette.success.main, 0.7)})`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <CheckCircleIcon
                        sx={{ fontSize: 32, color: "success.main" }}
                      />
                      <Typography
                        variant='h5'
                        sx={{ fontWeight: 700, color: "success.dark" }}
                      >
                        <CheckCircleOutlineIcon
                          sx={{ fontSize: 28, mr: 1, verticalAlign: "middle" }}
                        />
                        Successfully Tagged to FTC
                      </Typography>
                    </Box>
                    <Typography
                      variant='body1'
                      color='text.secondary'
                      sx={{ mb: 3, fontWeight: 500 }}
                    >
                      This foreign income is now included in Foreign Tax Credit
                      calculation. The system will automatically calculate your
                      maximum FTC claim.
                    </Typography>
                    {canEdit && (
                      <Button
                        variant='contained'
                        size='large'
                        onClick={onUnTagToFTC}
                        startIcon={<CancelIcon />}
                        sx={{
                          px: 4,
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "1rem",
                          bgcolor: "error.main",
                          color: "white",
                          borderColor: "error.main",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            bgcolor: "error.dark",
                            boxShadow: "0 6px 20px rgba(211, 47, 47, 0.3)",
                          },
                        }}
                      >
                        Remove from FTC
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
                    >
                      Step 3: Tag for Foreign Tax Credit Calculation
                    </Typography>
                    {!canEdit && (
                      <Alert
                        severity='info'
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          textAlign: "left",
                          maxWidth: 500,
                          mx: "auto",
                        }}
                      >
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                          This is currently not tagged to Foreign Tax Credit. If
                          you think this is an error, please contact your
                          administrator.
                        </Typography>
                      </Alert>
                    )}
                    {canEdit && (
                      <Button
                        variant='contained'
                        size='large'
                        onClick={handleTagToFTC}
                        disabled={!isTagToFTCEnabled}
                        startIcon={<CurrencyExchangeIcon />}
                        sx={{
                          px: 6,
                          py: 2,
                          borderRadius: 3,
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "1.1rem",
                          minWidth: 250,
                          background: `linear-gradient(135deg, ${
                            theme.palette.primary.main
                          }, ${alpha(theme.palette.primary.main, 0.8)})`,
                          boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            transform: "translateY(-3px)",
                            boxShadow: "0 8px 25px rgba(0,0,0,0.3)",
                          },
                          "&:disabled": {
                            background: theme.palette.action.disabledBackground,
                            color: theme.palette.action.disabled,
                          },
                        }}
                      >
                        Tag to FTC
                      </Button>
                    )}
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mt: 2, maxWidth: 500, mx: "auto" }}
                    >
                      This will include this foreign income in your Foreign Tax
                      Credit calculation, potentially reducing your Singapore
                      tax liability.
                    </Typography>
                    {!isTagToFTCEnabled && (
                      <Typography
                        variant='caption'
                        color='error.main'
                        sx={{ mt: 1, display: "block", fontWeight: 600 }}
                      >
                        <WarningIcon
                          sx={{ fontSize: 16, mr: 1, verticalAlign: "middle" }}
                        />
                        Please enter a foreign tax amount first
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Enhanced Income Calculation Summary */}
      <CalculationSummary
        steps={steps}
        total={total}
        title='Final Income Calculation'
        subtitle='Review the complete calculation breakdown'
      />
    </Box>
  );
};

export default ForeignIncomeForm;
